var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'root/src/client/v2/common/components/ui/dialog';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import { pointsEndpointIds } from 'root/src/shared/points/points.constants';
import { MIN_POINTS_PAYOUT_AMOUNT, MIN_POINTS_PAYOUT_AMOUNT_WITHOUT_FEE } from 'root/src/shared/constants/pointsCashOut';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { GET_CREATOR_POINTS_TRANSACTIONS } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { events } from 'root/src/shared/constants/analytics';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { formatPointsCashValue } from './usePointsTransactions';
// The pending-payout transaction row is written asynchronously (SQS) after INIT_POINTS_PAYOUT returns.
// Legacy waited 6s before closing so the list would show it; we instead refetch again after this delay.
const POINTS_PAYOUT_SETTLE_MS = 6000;
const minPointsMessage = `Minimal amount of points to payout equals to ${formatNumberWithCommas(MIN_POINTS_PAYOUT_AMOUNT)}`;
// Redesign port of the legacy PointsCashOutModal: confirm cashing out the creator's points. Shows the
// total points and their USD value, the payout notice, then a stronger second-step confirmation (an
// intentional addition on top of legacy) before firing INIT_POINTS_PAYOUT and refreshing the history.
export const SubmitPointsDialog = ({ open, onOpenChange, creatorPoints }) => {
    const reduceMotion = useReducedMotion();
    const { fetcher } = useDareDropApi();
    const { loading, success, error, run } = useAsyncAction();
    const queryClient = useQueryClient();
    const { userId } = useUserId();
    const [step, setStep] = useState(1);
    const belowMinimum = creatorPoints < MIN_POINTS_PAYOUT_AMOUNT;
    const cashValueLabel = formatPointsCashValue(creatorPoints);
    // Always reopen on the first (review) step; also reset after it closes.
    useEffect(() => {
        setStep(1);
    }, [open]);
    // Step 1 primary action: guard the minimum, then advance to the confirmation step (does NOT pay out).
    const advance = () => {
        if (belowMinimum) {
            toast.error(minPointsMessage);
            return;
        }
        setStep(2);
    };
    // Step 2 final action: actually submit the payout.
    const submit = () => {
        if (belowMinimum) {
            toast.error(minPointsMessage);
            return;
        }
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            // Suppress the fetcher's legacy toast; useAsyncAction surfaces the sonner loading/success/error.
            // The server re-validates (min points, an already-pending payout) and throws a message we surface.
            yield fetcher({ endpointId: pointsEndpointIds.INIT_POINTS_PAYOUT, payload: {} }, true);
            if (userId) {
                analyticsSendItem(events.pointsCashOut, userId);
            }
            yield queryClient.invalidateQueries({ queryKey: [GET_CREATOR_POINTS_TRANSACTIONS] });
            // Refetch again once the async pending-payout row has settled, so it appears without a refresh.
            setTimeout(() => {
                void queryClient.invalidateQueries({ queryKey: [GET_CREATOR_POINTS_TRANSACTIONS] });
            }, POINTS_PAYOUT_SETTLE_MS);
        }), {
            onSuccess: () => {
                onOpenChange(false);
                setStep(1);
            },
            toast: {
                loading: 'Submitting your points...',
                success: 'Points submitted. Expect PayPal delivery within 7 days.',
                error: caught => (caught instanceof Error ? caught.message : 'Could not submit points'),
            },
        });
    };
    const stepVariants = reduceMotion
        ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
        : { initial: { opacity: 0, x: 14 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -14 } };
    return (<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md gap-0 overflow-hidden border-white/[0.08] bg-card/95 p-0 backdrop-blur-xl">
				<DialogHeader className="border-b border-solid border-white/[0.06] px-5 py-4 text-left">
					<DialogTitle className="font-display text-base font-semibold">Submit your points</DialogTitle>
					<DialogDescription className="text-xs text-muted-foreground">Ready to submit?</DialogDescription>
				</DialogHeader>

				{/* Reserve height so the step cross-fade (mode="wait") never collapses the dialog (no CLS). */}
				<div className="min-h-[236px] px-5 py-5">
					<AnimatePresence mode="wait" initial={false}>
						{step === 1 ? (<motion.div key="review" initial={stepVariants.initial} animate={stepVariants.animate} exit={stepVariants.exit} transition={{ duration: 0.2, ease: 'easeOut' }} className="space-y-4">
								<div className="grid grid-cols-2 divide-x divide-white/[0.06] overflow-hidden rounded-xl border border-solid border-white/[0.06] bg-white/[0.02]">
									<div className="flex flex-col items-center gap-1 px-4 py-4 text-center">
										<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Total points</span>
										<span className="font-display text-xl font-semibold text-foreground">{formatNumberWithCommas(creatorPoints)}</span>
									</div>
									<div className="flex flex-col items-center gap-1 px-4 py-4 text-center">
										<span className="text-[11px] font-medium uppercase tracking-wide text-accent">Cash value</span>
										<span className="font-display text-xl font-semibold text-accent">{cashValueLabel}</span>
									</div>
								</div>

								<div className="space-y-2 rounded-lg border border-solid border-white/[0.06] bg-white/[0.02] p-3 text-xs text-muted-foreground">
									<p>The money will be deposited into your PayPal account within 7 days of processing.</p>
									<p>
										If you submit more than {formatNumberWithCommas(MIN_POINTS_PAYOUT_AMOUNT_WITHOUT_FEE)} at once, we pay all
										transaction fees. Otherwise, you pay the fees.
									</p>
								</div>

								{belowMinimum && <p className="text-xs font-medium text-neon-pink">{minPointsMessage}</p>}
							</motion.div>) : (<motion.div key="confirm" initial={stepVariants.initial} animate={stepVariants.animate} exit={stepVariants.exit} transition={{ duration: 0.2, ease: 'easeOut' }} className="flex min-h-[196px] flex-col items-center justify-center text-center">
								<h4 className="font-display text-lg font-semibold text-foreground">Are you sure?</h4>
								<p className="mt-2 max-w-xs text-sm text-muted-foreground">
									You are about to submit <span className="font-semibold text-foreground">{formatNumberWithCommas(creatorPoints)}</span> points
									(<span className="font-semibold text-accent">{cashValueLabel}</span>) to PayPal. This cannot be undone.
								</p>
							</motion.div>)}
					</AnimatePresence>
				</div>

				<div className="flex gap-2 border-t border-solid border-white/[0.06] px-5 py-4">
					{step === 1 ? (<>
							<Button variant="secondary" onClick={() => onOpenChange(false)} className="flex-1 rounded-lg text-sm">
								Cancel
							</Button>
							<Button variant="brand" disabled={belowMinimum} onClick={advance} className="flex-1 rounded-lg text-sm font-semibold">
								Submit points
							</Button>
						</>) : (<>
							<Button variant="secondary" onClick={() => setStep(1)} className="flex-1 rounded-lg text-sm">
								Back
							</Button>
							<Button variant="brand" loading={loading} success={success} error={error} onClick={submit} className="flex-1 rounded-lg text-sm font-semibold">
								Yes, submit
							</Button>
						</>)}
				</div>
			</DialogContent>
		</Dialog>);
};
